import { Login } from '@platform/client';
import { InlineFormInput } from '@platform/components';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { MovingWorldsBannerLogoLink } from '../../../layout/MovingWorldsLogo';
import repBackground from '../../background-images/RepBackground.png';
import { BackToOptionsButton } from '../../components/BackToOptionsButton';
import { ContinueButton } from '../../components/ContinueButton';
import { LogInBackground } from '../../components/LogInBackground';
import { OauthSignUpSegment } from '../../components/OauthSignUpSegment';
import { SetPasswordInput } from '../../components/SetPasswordInput';
import { ToSFormCheckbox } from '../../components/ToSCheckbox';
import { useSearch } from 'wouter';

type RepSignUpPageProps = {
  setRepresentativeSelected: (representativeSelected: boolean) => void;
};

type FormValues = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  termsOfService: boolean;
};

export const RepSignUpPage = (props: RepSignUpPageProps) => {
  const { setRepresentativeSelected } = props;
  const [accountExists, setAccountExists] = useState(false);

  // This parent_org_id handles legacy org sign up flow for F5 organizations
  // it will be used to create an organization_parents record later in legacy sign up flow on organization creation
  // it is passed here to use in the email verification link assuming user then proceeds through sign up
  const searchParams = useSearch();
  const urlSearchParams = new URLSearchParams(searchParams);
  const repPartnerId = urlSearchParams.get('partner_id');
  const orgInviteId = urlSearchParams.get('invite_id');

  const [isUsingEmail, setIsUsingEmail] = useState(false);

  const signUpMutation = Login.useSignUpWithPassword({
    mutation: {
      onSuccess: () => {
        // Redirect to the email verification page
        window.location.href =
          '/platform/auth/email-verification?email=' + getValues('email') + '/';
      },
      onError: (error) => {
        if ((error as AxiosError).status === 401) {
          setAccountExists(true);
        }
      },
    },
  });

  const onSignUpWithEmailClick = () => {
    setIsUsingEmail(true);
  };

  const signUp: SubmitHandler<FormValues> = async (data) => {
    signUpMutation.mutate({
      data: {
        password: data.password,
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        isRep: true,
        repPartnerId,
        orgInviteId,
      },
    });
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<FormValues>({ mode: 'onBlur', reValidateMode: 'onSubmit' });

  return (
    <LogInBackground background={repBackground}>
      <div className='h-full'>
        <div className='md:hidden w-full m-auto text-brand-500 h-16 min-h-16 border-b border-gray-100'>
          <MovingWorldsBannerLogoLink
            className='flex justify-center h-full w-full items-center'
            height='22'
          />
        </div>
        <div className='flex flex-col w-full md:w-[400px] px-4 md:px-1 py-8 md:py-0 h-full md:items-center md:justify-center'>
          <div className='flex flex-col gap-8'>
            <h3 className='text-display-sm font-gray-700 font-medium'>
              {orgInviteId ?
                'You have been invited to join an organization'
              : 'Create an account, grow your business'}
            </h3>

            {!isUsingEmail && (
              <OauthSignUpSegment
                isRep={true}
                onSignUpWithEmailClick={onSignUpWithEmailClick}
                orgInviteId={orgInviteId}
              />
            )}
            {isUsingEmail && (
              <form
                className='flex flex-col gap-8'
                name='rep-signup'
                onSubmit={handleSubmit(signUp)}
              >
                <div className='flex flex-row justify-between gap-4'>
                  <InlineFormInput
                    label='First name'
                    autocomplete='given-name'
                    placeholder='First name'
                    register={() => {
                      return register('firstName', {
                        required: { value: true, message: 'Enter a first name.' },
                      });
                    }}
                    errors={errors.firstName}
                  />
                  <InlineFormInput
                    label='Last name'
                    placeholder='Last name'
                    autocomplete='family-name'
                    register={() => {
                      return register('lastName', {
                        required: { value: true, message: 'Enter a last name.' },
                      });
                    }}
                    errors={errors.lastName}
                  />
                </div>
                <InlineFormInput
                  label='Email'
                  placeholder='Enter your email address'
                  autocomplete='email'
                  register={() => {
                    return register('email', {
                      required: { value: true, message: 'Please enter your email address.' },
                      pattern: {
                        value: /^\S+@\S+$/i,
                        message: 'Please enter a valid email address.',
                      },
                    });
                  }}
                  errors={errors.email}
                />
                <SetPasswordInput
                  password={getValues('password') || ''}
                  register={register}
                  errors={errors.password}
                />
                <ToSFormCheckbox register={register} errors={errors.termsOfService} />
                {accountExists && (
                  <div className='border border-gray-200 bg-error-25 shadows-xs h-[52px] flex gap-1 items-center p-4 text-sm'>
                    This account already exists.
                    <a href='/platform/auth/login' className='underline text-brand-100'>
                      Log in to continue
                    </a>
                  </div>
                )}
                <ContinueButton />
              </form>
            )}
            <BackToOptionsButton
              onClick={() => {
                setRepresentativeSelected(false);
              }}
            />
          </div>
        </div>
      </div>
    </LogInBackground>
  );
};
